import { useCallback, useEffect, useRef } from 'react';
import { ValidationProblemCode } from '@ydistri/api-sdk';
import { addToast } from '@ydistri/utils';
import { getSelectedProjectShortName } from '../../../../apis/apiHeader';
import { useAppDispatch } from '../../../../store';
import { problemMessagesOf, problemsOfError } from '../../../Configuration/Validator/problemsLib';
import { AvailableModals, openModal } from '../../../Modals/modalSlice';
import {
  GetEntityListRequest,
  usePreviewEntityListUnarchiveMutation,
  useUnarchiveEntityListMutation,
} from '../apiEntityLists';

export const useUnarchiveEntityList = () => {
  const dispatch = useAppDispatch();
  const [preview, { isLoading: isPreviewing }] = usePreviewEntityListUnarchiveMutation();
  const [update, { isLoading: isUpdating }] = useUnarchiveEntityListMutation();
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const unarchive = useCallback(
    async (
      request: GetEntityListRequest & { projectShortName?: string },
      isComputationConfirmed = false,
    ): Promise<boolean> => {
      const projectShortName = request.projectShortName ?? getSelectedProjectShortName();
      if (
        !projectShortName ||
        projectShortName !== getSelectedProjectShortName() ||
        inFlight.current
      )
        return false;
      inFlight.current = true;
      const scopedRequest = { ...request, projectShortName };
      const isCurrent = () => mounted.current && getSelectedProjectShortName() === projectShortName;
      const confirm = () =>
        dispatch(
          openModal({
            type: AvailableModals.ENTITY_LIST_UNARCHIVE_CONFIRMATION,
            data: scopedRequest,
          }),
        );
      try {
        if (!isComputationConfirmed) {
          const needsComputation = await preview(scopedRequest).unwrap();
          if (!isCurrent()) return false;
          if (needsComputation) {
            confirm();
            return false;
          }
        }
        await update({ ...scopedRequest, isComputationConfirmed }).unwrap();
        if (!isCurrent()) return false;
        dispatch(addToast({ message: 'List unarchived' }));
        return true;
      } catch (error) {
        if (isCurrent()) {
          if (
            !isComputationConfirmed &&
            problemsOfError(error).some(
              problem =>
                problem.code === ValidationProblemCode.EntityListComputationConfirmationRequired,
            )
          )
            confirm();
          else
            dispatch(
              addToast({
                isError: true,
                message: 'The list could not be unarchived',
                description: problemMessagesOf(error, 'Please try again.'),
              }),
            );
        }
        return false;
      } finally {
        inFlight.current = false;
      }
    },
    [dispatch, preview, update],
  );

  return { unarchive, isLoading: isPreviewing || isUpdating };
};
